import { useRef } from "react";
import H2Base from "../../../components/ui/titles/H2Base";

// Palette des classes de risque du projet (voir getRiskClassColor() dans
// pages/Home/functions/projectCardFunctions.js) : A et B sont au vert,
// C+ au jaune et C-, le plus risque propose, a l'orange.
const ratings = [
    { note: "A", badgeClass: "bg-green-500 text-white", description: "Entreprise très solide, historique long, faible probabilité de défaut." },
    { note: "B", badgeClass: "bg-green-500 text-white", description: "Bon profil financier, risque modéré." },
    { note: "C+", badgeClass: "bg-yellow-500 text-secondaryDark", description: "Profil plus risqué, croissance récente ou secteur cyclique." },
    { note: "C-", badgeClass: "bg-orange-500 text-white", description: "Risque le plus élevé proposé sur la plateforme." },
];

// Visuel illustratif de la carte projet d'exemple, servi depuis public/.
const PROJECT_PHOTO_URL = "/images/pages/investir/logo_pharma.jpg";
// Fiche du projet sur l'espace client. URL absolue volontaire : REACT_APP_USER_DASHBOARD
// pointe sur la preprod dans certains environnements, or ce projet d'exemple est figé.
const PROJECT_URL = "https://monespace.pretup.fr/pharmaciedemametz-5640";

const projectMeta = [
    { key: "Montant", value: "86 016 €" },
    { key: "Taux d'intérêt", value: "9,50 %" },
    { key: "Durée", value: "24 mois" },
];

const RiskRating = () => {
    const legendRef = useRef(null);

    const scrollToLegend = () => {
        legendRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    return (
        <section className="py-16 md:py-20">
            <div className="max-w-2xl">
                <H2Base text="Chaque projet, sa notation expliquée" tailwindClass="text-2xl md:text-3xl" />
                <p className="leading-relaxed text-gray-600">
                    Une lettre de notation ne s'affiche jamais seule : vous pouvez toujours comprendre, en un coup
                    d'œil, ce qu'elle signifie.
                </p>
            </div>

            <div className="mt-10 grid grid-cols-1 items-start gap-8 lg:grid-cols-2">
                <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-lg">
                    <div className="h-36 bg-gradient-to-br from-secondaryLight to-secondary">
                        <img src={PROJECT_PHOTO_URL} alt="" className="h-full w-full object-cover" />
                    </div>
                    <div className="p-6">
                        <div className="mb-3 flex flex-wrap gap-2">
                            {["Développement", "Crédit amortissable"].map((tag) => (
                                <span key={tag} className="rounded-md bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-600">
                                    {tag}
                                </span>
                            ))}
                        </div>
                        <h3 className="mb-4 text-xl font-bold text-secondaryDark">
                            <a
                                href={PROJECT_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="transition hover:text-secondary hover:underline"
                            >
                                Pharmacie Mametz
                            </a>
                        </h3>

                        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                            {projectMeta.map(({ key, value }) => (
                                <div key={key} className="contents">
                                    <dt className="text-gray-600">{key}</dt>
                                    <dd className="font-semibold text-black">{value}</dd>
                                </div>
                            ))}

                            <dt className="text-gray-600">Risque</dt>
                            <dd>
                                <button
                                    type="button"
                                    onClick={scrollToLegend}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-yellow-300 bg-yellow-50 px-2.5 py-1 text-xs font-bold text-yellow-700 transition hover:bg-yellow-100"
                                >
                                    C+
                                </button>
                            </dd>

                            <dt className="text-gray-600">Garantie</dt>
                            <dd>
                                <span className="inline-flex items-center rounded-full border border-gray-200 bg-secondaryExtraLight/50 px-2.5 py-1 text-xs font-bold text-secondary">
                                    Caution dirigeant
                                </span>
                            </dd>
                        </dl>
                    </div>
                </div>

                <div ref={legendRef} className="rounded-2xl border border-gray-200 bg-white p-7">
                    <h3 className="mb-4 text-xs font-bold uppercase tracking-wide text-gray-600">
                        Échelle de risque PretUp — ce que signifie chaque note
                    </h3>
                    <ul className="divide-y divide-gray-200">
                        {ratings.map(({ note, badgeClass, description }) => (
                            <li key={note} className="flex items-center gap-4 py-3">
                                <span className={`w-12 flex-shrink-0 rounded-md py-1 text-center font-bold ${badgeClass}`}>
                                    {note}
                                </span>
                                <span className="text-sm text-black">{description}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
};

export default RiskRating;
